import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { motion } from "framer-motion";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import toast, { Toaster } from "react-hot-toast";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";
import configServ from "../../services/config";
import CustomerDetails from "../../Components/Card/Diagnosis/CustomerDetails";
import { useRef } from "react";
import UploadReportsTable from "./UploadReportsTable";
import { useEffect } from "react";
import PainAreaForm from "../../Components/Card/Customers/PainAreaForm";
import ImageZoomDialog from "../../Components/AlertDialog/ImageZoomDialog";

// Dummy API and customer ID placeholder

const customerId = 123; // replace with actual logic

const UploadReports = () => {
  const prescriptionRef = useRef(); // ref for child

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [formData, setFormData] = useState({
    description: "",
    file: null,
    fileType: "",
    reportName: "",
    sendMail: false,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState([]);

  const [bactLoader, setBactLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);

  const [items, setItems] = useState([]);
  const [selectedPainArea, setSelectedPainArea] = useState(null);

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");


  const handleClickOpen = () => {
    setOpenDialog(!openDialog);
    clear();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!file) return;

    const fileType = file.type.startsWith("image") ? "image" : "pdf";

    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Only PDF, PNG, or JPG files are allowed.");
      return;
    }

    if (file.size > maxSize) {
      toast.error("File size must be less than 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, file: reader.result, fileType }));
    };
  };

  const openPDF = (base64) => {
    const byteCharacters = atob(base64.split(",")[1]);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }

    const byteArray = new Uint8Array(byteArrays);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const blobURL = URL.createObjectURL(blob);

    window.open(blobURL, "_blank");
  };

  const handleDelete = () => {
    setFormData((prev) => ({ ...prev, file: null, fileType: "" }));
  };

  const clear = () => {
    setFormData({
      description: "",
      file: null,
      fileType: "",
      sendMail: false,
    });
    setIsEdit(false);
    prescriptionRef.current?.handleReset();
    setSelectedUserId(null);
    setEditData([]);
    setSelectedPainArea(null);
  };

  const handleSave = async () => {
    if (!formData.file) {
      toast.error("Please upload a PDF or image.");
      return;
    }

    // if (!formData.description.trim()) {
    //   toast.error("Please enter a description.");
    //   return;
    // }

    if (!formData.reportName.trim()) {
      toast.error("Please enter a reportName.");
      return;
    }

    setBactLoader(true);
    try {
      const payload = {
        description: formData.description,
        user_id: selectedUserId,
        file: formData.file,
        is_admin: true,
        reportName: formData.reportName,
        item_id: selectedPainArea,
        sendMail: formData.sendMail,
      };

      if (isEdit && editData?.id) {
        payload.id = editData.id;
        const response = await configServ.updateReport(payload);
        if (response.success) {
          toast.success("Report updated successfully!");
        } else {
          toast.error("Failed to update report.");
        }
      } else {
        const response = await configServ.storeReport(payload);
        if (response.success) {
          toast.success("Report saved successfully!");
        } else {
          toast.error("Failed to save report.");
        }
      }

      clear();
      handleClickOpen();
      fetchReports(); // optional reload function
    } catch (error) {
      toast.error("Something went wrong!");
    } finally {
      setBactLoader(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);

    // console.log("Selected User ID:", userId);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await configServ.getAllReports();
      if (data.success) setReports(data.reports); // depends on your backend response structure
    } catch (error) {
      // handle error if needed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // You can call fetchReports() here when needed
    fetchReports();
  }, []);

  const handleEditData = (rowData) => {
    scrollToTop();
    setIsEdit(true);
    setOpenDialog(true);
    setEditData(rowData);
    setFormData({
      description: rowData.description || "",
      file: rowData.file_base64 || null,
      reportName: rowData.reportName || null,
      fileType: rowData.file_base64?.startsWith("data:application/pdf")
        ? "pdf"
        : "image",
    });
    setSelectedPainArea(rowData.item_id);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDeleteData = async (rowData) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this report?"
    );
    if (!confirmDelete) return;

    try {
      const response = await configServ.deleteReport({ id: rowData.id });
      if (response.success) {
        toast.success("Report deleted successfully!");
        fetchReports(); // refresh the report list
      } else {
        toast.error("Failed to delete report.");
      }
    } catch (error) {
      toast.error("Something went wrong during deletion.");
    }
  };

  const fetchCustomerItems = async () => {
    try {
      // setLoadingg(true);
      const dataToSend = {
        customer_id: selectedUserId,
      };
      const res = await configServ.getActiveDiagnosesItemsforCustomer(
        dataToSend
      );
      if (res.success) {
        setItems(res.data);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      // setLoadingg(false);
    }
  };

  useEffect(() => {
    if (selectedUserId && openDialog) {
      fetchCustomerItems();
    }
  }, [selectedUserId, openDialog]);

  return (
    <>
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"Upload Reports"}
        handlePopup={handleClickOpen}
        create={openDialog}
      />

      <ImageZoomDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        imageUrl={selectedImageUrl}
      />

      {openDialog && (
        <motion.div
          style={{
            width: "100%",
            backgroundColor: "#e3f3fc",
            marginTop: 0,
            padding: 20,
            borderRadius: "10px",
            border: "none",
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.1, delay: 0.1 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomerDetails
                onUserSelect={handleUserSelect}
                ref={prescriptionRef}
                //   diagnosisData={diagnosisData}
                editData={editData}
              />
            </Grid>

            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                Lab Report Details
              </Typography>

              <Grid container spacing={2} alignItems="flex-start">
                {/* Pain Area Dropdown */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="pain-area-label">Pain Area</InputLabel>
                    <Select
                      labelId="pain-area-label"
                      value={selectedPainArea || ""}
                      label="Pain Area"
                      sx={{ backgroundColor: "white" }}
                      onChange={(e) => {
                        setSelectedPainArea(e.target.value); // just store ID
                      }}
                    >
                      {items?.diagnosis?.map((item) => (
                        <MenuItem key={item.item_id} value={item.item_id}>
                          {item.item_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Lab Report Name */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Lab Report Name"
                    name="reportName"
                    placeholder="Enter report name like Blood Test"
                    value={formData.reportName}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ backgroundColor: "white", mt: 1 }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    placeholder="Enter description like xray, sugar etc."
                    value={formData.description}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ backgroundColor: "white", mt: 1 }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.sendMail}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sendMail: e.target.checked,
                          })
                        }
                        name="sendMail"
                        color="primary"
                      />
                    }
                    label="Send Mail"
                    sx={{ mt: 1 }}
                  />
                </Grid>

                {/* Upload Button */}
                <Grid item xs={12} md={3}>
                  <Box mt={1}>
                    <label htmlFor="upload-file">
                      <input
                        id="upload-file"
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <Button
                        component="span"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ backgroundColor: "white" }}
                      >
                        Upload File
                      </Button>
                    </label>
                  </Box>
                </Grid>

                {/* Uploaded Preview */}
                {formData.file && (
                  <Grid item xs={12} md={3}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{
                        p: 2,
                        backgroundColor: "#f5f5f5",
                        borderRadius: 2,
                        mt: 1,
                      }}
                    >
                      {formData.fileType === "pdf" ? (
                        <IconButton onClick={() => openPDF(formData.file)}>
                          <PictureAsPdfIcon
                            sx={{ fontSize: 40, color: "red" }}
                          />
                        </IconButton>
                      ) : (
                        <img
                          src={formData.file}
                          onClick={() => {
                            setSelectedImageUrl(formData.file);
                            setImageDialogOpen(true);
                          }}
                          alt="Uploaded"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      )}
                      <IconButton onClick={handleDelete}>
                        <DeleteIcon sx={{ color: "red" }} />
                      </IconButton>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Buttons */}
            <Grid
              container
              item
              xs={12}
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  size="small"
                  onClick={clear}
                  variant="contained"
                  color="secondary"
                  startIcon={<RotateLeftIcon />}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  size="small"
                  onClick={handleSave}
                  variant="contained"
                  color={isEdit ? "success" : "primary"}
                  startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
                  disabled={bactLoader}
                >
                  {isEdit ? "Update" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      )}

      <UploadReportsTable
        reports={reports}
        loading={loading}
        handleEditData={handleEditData}
        handleDeleteData={handleDeleteData}
      />
    </>
  );
};

export default UploadReports;
